import detectEthereumProvider from "@metamask/detect-provider";
import { signatureToPubkey } from "@hanchon/signature-to-pubkey";
import {
  createTxRawEIP712,
  signatureToWeb3Extension,
} from "@tharsis/transactions";
import * as evmosLib from "../lib/evmos";
import { coins } from "../constants";

const Account = ({ ethereum }, args = {}) => {
  const getAddress = async () => {
    const accounts = await ethereum.request({
      method: "eth_requestAccounts",
    });
    if (!accounts.length) throw new Error("No account found.");
    const address = accounts[0];
    let pubkey = args.pubkey;
    if (!pubkey) {
      pubkey = await getPubKey(address);
    }
    return {
      address: evmosLib.ethToCosmosAddress(address, args.hrpAddress),
      metadata: {
        type: "metamaskEvmos",
        pubkey,
        ethAddress: address,
      },
    };
  };

  /* istanbul ignore next */
  const signTransaction = async (rawTx, { address, ethChainId }) => {
    const eipToSign = evmosLib.getEipToSign(rawTx, {
      ethChainId,
      address,
    });
    let signature = await ethereum.request({
      method: "eth_signTypedData_v4",
      params: [evmosLib.cosmosToEthAddress(address), JSON.stringify(eipToSign)],
    });
    const { body, authInfo } = evmosLib.getAminoPayload(rawTx, {
      pubkey: args.pubkey,
      path: args.typeUrl.slice(1), // remove the "/"
    });
    const extension = signatureToWeb3Extension(
      {
        chainId: ethChainId,
        cosmosChainId: rawTx.chain_id,
      },
      {
        accountAddress: address,
        sequence: Number(rawTx.sequence),
        accountNumber: Number(rawTx.account_number),
        pubkey: args.pubkey,
      },
      signature
    );
    extension.path = args.web3TypeUrl.slice(1);
    const txRaw = createTxRawEIP712(body, authInfo, extension);
    return {
      tx_bytes: Array.from(txRaw.message.serializeBinary()),
    };
  };

  const getPubKey = async (address) => {
    const signature = await ethereum.request({
      method: "personal_sign",
      params: [address, "generate_pubkey"],
    });
    const message = Buffer.from([
      50, 215, 18, 245, 169, 63, 252, 16, 225, 169, 71, 95, 254, 165, 146, 216,
      40, 162, 115, 78, 147, 125, 80, 182, 25, 69, 136, 250, 65, 200, 94, 178,
    ]);
    return signatureToPubkey(signature, message);
  };

  const sign = async (address, message) => {
    return ethereum.request({
      method: "personal_sign",
      params: [address, message],
    });
  };

  /* istanbul ignore next */
  const verifySignature = async (message, signature) => {};

  return {
    getAddress,
    getPubKey,
    sign,
    signTransaction,
    verifySignature,
  };
};

const Backend = ({ ethereum, coinName }) => {
  const genAccount = async (args) => Account({ ethereum }, args);

  const getAccount = async (args) => {
    const accountPayload = coins[coinName];
    return genAccount({ ...args, ...accountPayload });
  };
  const cleanUp = async () => {};

  return {
    getAccount,
    cleanUp,
  };
};

Backend.create = async ({ metamaskChainParams, coinName } = {}) => {
  const ethereum = await detectEthereumProvider();
  if (!ethereum) {
    throw new Error("Metamask is not installed.");
  }
  const accounts = await ethereum.request({ method: "eth_requestAccounts" });
  if (!accounts.length) throw new Error("No account found.");
  if (metamaskChainParams) {
    try {
      await ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: metamaskChainParams.chainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        await ethereum.request({
          method: "wallet_addEthereumChain",
          params: [metamaskChainParams, accounts[0]],
        });
      }
    }
  }
  return Backend({ ethereum, coinName });
};

export default Backend;
